import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Camera, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CurrencyInput } from '@/components/ui/currency-input'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_CONTROL_SELECT_OPEN_CLASS,
} from '@/components/ui/form-control-styles'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { EntityPicker } from '@/components/shared/EntityPicker'
import { ServiceItemImage } from '@/components/shared/ServiceItemPicker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTaxRateOptions } from '@/hooks/useSettings'
import { useCreateQuoteServiceItem } from '@/hooks/useQuotes'
import { settingsApi } from '@/services/settings'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { parseCurrencyInput } from '@/lib/format'
import { TAX_RATES, computeLineTotal } from '@/lib/lineItems'
import { cn } from '@/lib/utils'
import type { LineItem } from '@/types'

const drawerClass =
  'tf-right-drawer-short [&>button]:right-6 [&>button]:top-4 [&>button]:rounded-xl [&>button]:bg-[#f4f5f8] [&>button]:p-2 [&>button]:opacity-100 [&>button]:text-[#555b6a]'

const cardShadowClass =
  'shadow-[0_1px_4px_-1px_rgba(38,45,66,0.08),0_1px_8px_-1px_rgba(38,45,66,0.12)]'

const fieldClass = cn(
  'h-11 rounded-xl border-transparent bg-[#f4f5f8] px-4 text-sm font-normal leading-[1.4] text-[#171a20] placeholder:font-normal placeholder:text-[#98a2b3]',
  FORM_CONTROL_FOCUS_CLASS
)

const textareaClass = cn(
  'min-h-[88px] rounded-xl border-transparent bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] placeholder:text-[#98a2b3]',
  FORM_CONTROL_FOCUS_CLASS
)

const selectClass = cn(
  'h-11 rounded-xl border-transparent bg-[#f4f5f8] px-4 text-sm font-normal leading-[1.4] text-[#171a20]',
  FORM_CONTROL_SELECT_OPEN_CLASS
)

const photoButtonClass =
  'h-10 rounded-xl border-dashed border-primary px-4 text-sm font-medium leading-none text-primary hover:bg-primary/5 hover:text-primary'

function DrawerFieldLabel({
  htmlFor,
  required,
  children,
}: {
  htmlFor?: string
  required?: boolean
  children: string
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className='text-xs font-medium leading-4 text-[#171a20]'
    >
      {children}
      {required && <span className='text-[#d63b33]'> *</span>}
    </Label>
  )
}

function DrawerShell({
  open,
  onOpenChange,
  title,
  informationTitle,
  children,
  footer,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  informationTitle: string
  children: ReactNode
  footer: ReactNode
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent side='right' className={drawerClass}>
        <div className='flex h-full min-h-0 flex-col'>
          <div className='tf-right-drawer-header tf-right-drawer-header-accent'>
            <DialogTitle className='pr-16 font-heading text-base font-semibold leading-[1.2] text-[#171a20]'>
              {title}
            </DialogTitle>
          </div>

          <div className='min-h-0 flex-1 overflow-y-auto bg-[#f4f5f8] px-6 py-6'>
            <div
              className={cn(
                'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white',
                cardShadowClass
              )}
            >
              <div className='relative flex items-center gap-3 px-4 pt-4'>
                <span className='absolute left-0 top-4 h-4 w-1 rounded-full bg-primary' />
                <span className='font-heading text-sm font-semibold leading-[1.2] text-[#3d434e]'>
                  {informationTitle}
                </span>
              </div>

              <div className='space-y-6 px-4 py-4'>{children}</div>
            </div>
          </div>

          <div className='tf-right-drawer-footer justify-end'>{footer}</div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

type ServiceFormState = {
  service_category_id: string
  name: string
  description: string
  unit_price: string
  unit: string
  tax_rate: string
}

type ServiceFormErrors = {
  service_category_id?: string
  name?: string
  unit_price?: string
  unit?: string
  tax_rate?: string
}

const EMPTY_SERVICE_FORM: ServiceFormState = {
  service_category_id: '',
  name: '',
  description: '',
  unit_price: '',
  unit: '',
  tax_rate: '',
}

export function AddQuoteServiceDrawer({
  open,
  onOpenChange,
  onAdd,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAdd: (item: LineItem) => void
}) {
  const { t } = useTranslation('quotes')
  const { data: taxOptions } = useTaxRateOptions()
  const createServiceItem = useCreateQuoteServiceItem()
  const portalHostRef = useRef<HTMLButtonElement | null>(null)
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  )
  const vatRates = useMemo(() => {
    const configured = taxOptions?.data?.map(option => option.rate) ?? []
    return (configured.length > 0 ? configured : [...TAX_RATES]).sort(
      (left, right) => left - right
    )
  }, [taxOptions])

  const defaultVatRate = useMemo(() => {
    return vatRates.includes(20) ? 20 : (vatRates[0] ?? 20)
  }, [vatRates])
  const buildEmptyServiceForm = (): ServiceFormState => ({
    ...EMPTY_SERVICE_FORM,
    tax_rate: String(defaultVatRate),
  })
  const [form, setForm] = useState<ServiceFormState>(() =>
    buildEmptyServiceForm()
  )
  const [errors, setErrors] = useState<ServiceFormErrors>({})
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    setPortalContainer(portalHostRef.current)
  }, [])

  useEffect(() => {
    return () => {
      if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview)
    }
  }, [imagePreview])

  const resetFormState = () => {
    setForm(buildEmptyServiceForm())
    setErrors({})
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const close = () => {
    setImagePreview(current => {
      if (current?.startsWith('blob:')) URL.revokeObjectURL(current)
      return null
    })
    resetFormState()
    onOpenChange(false)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      close()
      return
    }
    onOpenChange(true)
  }

  const pickImage = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    const nextPreview = URL.createObjectURL(file)
    setImagePreview(current => {
      if (current?.startsWith('blob:')) URL.revokeObjectURL(current)
      return nextPreview
    })
  }

  const removeImage = () => {
    setImagePreview(current => {
      if (current?.startsWith('blob:')) URL.revokeObjectURL(current)
      return null
    })
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const validate = () => {
    const next: ServiceFormErrors = {}
    if (!form.service_category_id)
      next.service_category_id = t(
        'form.manualServiceDrawer.validation.categoryRequired'
      )
    if (!form.name.trim())
      next.name = t('form.manualServiceDrawer.validation.nameRequired')
    if (parseCurrencyInput(form.unit_price) === null)
      next.unit_price = t(
        'form.manualServiceDrawer.validation.unitPriceRequired'
      )
    if (!form.unit.trim())
      next.unit = t('form.manualServiceDrawer.validation.unitRequired')
    if (!form.tax_rate)
      next.tax_rate = t('form.manualServiceDrawer.validation.vatRequired')
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const save = async () => {
    if (!validate()) return

    const unitPrice = parseCurrencyInput(form.unit_price) ?? 0
    const taxRate = Number(form.tax_rate || defaultVatRate)
    const resolvedTaxRate = Number.isFinite(taxRate) ? taxRate : defaultVatRate
    const resolvedTaxRateId =
      taxOptions?.data?.find(option => option.rate === resolvedTaxRate)?.id ??
      undefined

    try {
      const res = await createServiceItem.mutateAsync({
        service_category_id: form.service_category_id,
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        unit_price: unitPrice,
        unit: form.unit.trim() || undefined,
        tax_rate_id: resolvedTaxRateId,
        is_active: true,
        image: fileInputRef.current?.files?.[0] ?? undefined,
      })
      const item = res.data
      const persistedTaxRate =
        resolvedTaxRateId == null
          ? resolvedTaxRate
          : (item?.tax_rate ?? resolvedTaxRate)
      onAdd({
        service_item_id: item?.id ?? null,
        description: item?.name ?? form.name.trim(),
        service_description:
          item?.description ?? (form.description.trim() || null),
        image_file_id: item?.image_file_id ?? null,
        quantity: 1,
        unit_price: item?.unit_price ?? unitPrice,
        tax_rate: persistedTaxRate,
        line_total: computeLineTotal({
          quantity: 1,
          unit_price: item?.unit_price ?? unitPrice,
          tax_rate: persistedTaxRate,
        }),
        image_url: item?.image_url ?? imagePreview,
      })
      toast({
        variant: 'success',
        title: res.message,
      })
      close()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error),
      })
    }
  }

  return (
    <DrawerShell
      open={open}
      onOpenChange={handleOpenChange}
      title={t('form.manualServiceDrawer.title')}
      informationTitle={t('form.manualServiceDrawer.information')}
      footer={
        <div className='flex items-center justify-end gap-3'>
          <Button
            type='button'
            variant='outline'
            className='h-10 rounded-xl border-[#e7e9ef] px-4 text-sm font-medium leading-none text-[#171a20]'
            onClick={close}
          >
            {t('form.manualServiceDrawer.cancel')}
          </Button>
          <Button
            type='button'
            className='h-10 rounded-xl bg-primary px-4 text-sm font-medium leading-none text-white hover:bg-primary/90'
            onClick={() => void save()}
            disabled={createServiceItem.isPending}
          >
            {t('form.manualServiceDrawer.save')}
          </Button>
        </div>
      }
    >
      <div className='space-y-2'>
        <DrawerFieldLabel>
          {t('form.manualServiceDrawer.image')}
        </DrawerFieldLabel>
        <input
          ref={fileInputRef}
          type='file'
          accept='image/png,image/jpeg,image/jpg'
          className='hidden'
          onChange={pickImage}
        />
        <div className='flex flex-wrap items-center gap-4'>
          <button
            type='button'
            className='flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-[#e7e9ef] bg-[#f4f5f8]'
            onClick={() => fileInputRef.current?.click()}
          >
            <ServiceItemImage
              src={imagePreview}
              className='size-full rounded-xl bg-[#fff1ea]'
            />
          </button>
          <div className='space-y-1'>
            <div className='flex flex-wrap items-center gap-3'>
              <Button
                type='button'
                variant='outline'
                className={photoButtonClass}
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera className='mr-2 size-4' />
                {t('form.manualServiceDrawer.addPhoto')}
              </Button>
              {imagePreview && (
                <Button
                  type='button'
                  variant='outline'
                  className='h-10 rounded-xl border-[#b91c1c] px-3 text-sm font-medium leading-none text-[#991b1b] hover:bg-[#fef2f2] hover:text-[#991b1b]'
                  onClick={removeImage}
                >
                  <Trash2 className='mr-2 size-4' />
                  {t('form.manualServiceDrawer.removeImage')}
                </Button>
              )}
            </div>
            <p className='text-xs font-medium leading-4 text-[#6c7285]'>
              {t('form.manualServiceDrawer.imageHint')}
            </p>
          </div>
        </div>
      </div>

      <div className='space-y-2'>
        <DrawerFieldLabel htmlFor='quote-service-name' required>
          {t('form.manualServiceDrawer.name')}
        </DrawerFieldLabel>
        <Input
          id='quote-service-name'
          value={form.name}
          maxLength={150}
          placeholder={t('form.manualServiceDrawer.namePlaceholder')}
          className={cn(
            fieldClass,
            errors.name && 'border border-destructive bg-white'
          )}
          onChange={event => {
            setForm(current => ({ ...current, name: event.target.value }))
            setErrors(current => ({ ...current, name: undefined }))
          }}
        />
        {errors.name && (
          <p className='text-sm text-destructive'>{errors.name}</p>
        )}
      </div>

      <div className='space-y-2'>
        <DrawerFieldLabel htmlFor='quote-service-description'>
          {t('form.manualServiceDrawer.description')}
        </DrawerFieldLabel>
        <Textarea
          id='quote-service-description'
          value={form.description}
          maxLength={500}
          placeholder={t('form.manualServiceDrawer.descriptionPlaceholder')}
          className={textareaClass}
          onChange={event =>
            setForm(current => ({
              ...current,
              description: event.target.value,
            }))
          }
        />
      </div>

      <div className='space-y-2'>
        <DrawerFieldLabel htmlFor='quote-service-category' required>
          {t('form.manualServiceDrawer.category')}
        </DrawerFieldLabel>
        <EntityPicker
          queryKey='quote-service-categories'
          fetcher={({ search, limit, page }) =>
            settingsApi.listServiceCategories({ search, limit, page })
          }
          getOptionLabel={category => category.name}
          getOptionValue={category => category.id}
          value={form.service_category_id || null}
          onChange={value => {
            setForm(current => ({ ...current, service_category_id: value }))
            setErrors(current => ({
              ...current,
              service_category_id: undefined,
            }))
          }}
          placeholder={t('form.manualServiceDrawer.categoryPlaceholder')}
          className={cn(
            selectClass,
            !form.service_category_id && 'text-[#98a2b3]',
            errors.service_category_id && 'border border-destructive bg-white'
          )}
          portalContainer={portalContainer}
        />
        {errors.service_category_id && (
          <p className='text-sm text-destructive'>
            {errors.service_category_id}
          </p>
        )}
      </div>

      <div className='grid gap-4 sm:grid-cols-2'>
        <div className='space-y-2'>
          <DrawerFieldLabel htmlFor='quote-service-unit-price' required>
            {t('form.manualServiceDrawer.unitPrice')}
          </DrawerFieldLabel>
          <CurrencyInput
            id='quote-service-unit-price'
            value={form.unit_price}
            showEuroPrefix
            prefixClassName='text-sm font-normal'
            className={cn(
              fieldClass,
              errors.unit_price && 'border border-destructive bg-white'
            )}
            onValueChange={value => {
              setForm(current => ({ ...current, unit_price: value }))
              setErrors(current => ({ ...current, unit_price: undefined }))
            }}
          />
          {errors.unit_price && (
            <p className='text-sm text-destructive'>{errors.unit_price}</p>
          )}
        </div>

        <div className='space-y-2'>
          <DrawerFieldLabel htmlFor='quote-service-unit' required>
            {t('form.manualServiceDrawer.unit')}
          </DrawerFieldLabel>
          <Input
            id='quote-service-unit'
            value={form.unit}
            maxLength={80}
            placeholder={t('form.manualServiceDrawer.unitPlaceholder')}
            className={cn(
              fieldClass,
              errors.unit && 'border border-destructive bg-white'
            )}
            onChange={event => {
              setForm(current => ({ ...current, unit: event.target.value }))
              setErrors(current => ({ ...current, unit: undefined }))
            }}
          />
          {errors.unit && (
            <p className='text-sm text-destructive'>{errors.unit}</p>
          )}
        </div>
      </div>

      <div className='space-y-2'>
        <DrawerFieldLabel required>
          {t('form.manualServiceDrawer.vatRate')}
        </DrawerFieldLabel>
        <Select
          value={form.tax_rate || String(defaultVatRate)}
          onValueChange={value => {
            setForm(current => ({ ...current, tax_rate: value }))
            setErrors(current => ({ ...current, tax_rate: undefined }))
          }}
        >
          <SelectTrigger
            ref={portalHostRef}
            className={cn(
              selectClass,
              'w-[112px] min-w-[112px]',
              errors.tax_rate && 'border border-destructive bg-white'
            )}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className='rounded-xl border-[#e7e9ef] p-2'>
            {vatRates.map(rate => (
              <SelectItem
                key={rate}
                value={String(rate)}
                className='rounded-lg py-2'
              >
                {rate}%
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.tax_rate && (
          <p className='text-sm text-destructive'>{errors.tax_rate}</p>
        )}
      </div>
    </DrawerShell>
  )
}
